import { useCallback, useMemo, useState } from "react";

import { getBookingInstate } from "@repo/ui/api/getBookingInstate";
import { removeSssrsFromBookingInState } from "@repo/ui/api/removeSssrsFromBookingInState";
import { sellSSRs } from "@repo/ui/api/sellSsrs";
import { DIRECTIONS } from "@repo/ui/constants/directions";
import { SSR_CODES } from "@repo/ui/constants/ssrs";
import type { SsrDetail } from "@repo/ui/types/ssr";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import {
  selectSsrAvailability,
  setBookingDetails,
  setFlexiSmartButtonStatus,
} from "@repo/store/slices";

interface Params {
  tabSessionId: string;
  currencyCode: string;
  direction: string;
}

type ToggleParams = {
  isAdded: boolean;
  isCheckin: boolean;
  price: number;
  cardKey: string;
};

export function useFlexiSmartSSR({ tabSessionId, currencyCode, direction }: Params) {
  const [loading, setLoading] = useState(false);

  const ssrAvailability = useAppSelector(selectSsrAvailability);
  const dispatch = useAppDispatch();
  /**
   * 1. Get journey based on direction
   */
  const journeySsrs = useMemo(() => {
    const journeys = ssrAvailability?.journeySsrs ?? [];

    if (direction === DIRECTIONS.OUTBOUND) {
      return journeys[0]; // outbound
    }

    if (direction === DIRECTIONS.INBOUND) {
      return journeys[1]; // inbound
    }

    return undefined;
  }, [ssrAvailability, direction]);

  /**
   * 2. Extract FLXB SSR
   */
  const flexiSsr = useMemo(() => {
    return journeySsrs?.ssrs?.find((s: SsrDetail) => s.ssrCode === SSR_CODES.FLEXI);
  }, [journeySsrs]);

  /**
   * 3. Build passenger payload
   */
  const buildPayload = useCallback(() => {
    if (!flexiSsr?.passengersAvailability) return [];

    return Object.values(flexiSsr.passengersAvailability)
      .map((p) => p.ssrKey)
      .filter((key): key is string => key != null);
  }, [flexiSsr]);

  /**
   * 4. Add / Remove SSR
   */
  const toggleFlexiSmart = useCallback(
    async ({ isAdded, isCheckin, price, cardKey }: ToggleParams) => {
      if (loading || !flexiSsr) return;

      const nextState = !isAdded;
      const cartType = isCheckin ? "checkInCart" : "bookingCart";

      setLoading(true);

      try {
        if (nextState) {
          const ssrKeys = buildPayload();

          if (ssrKeys.length > 0) await sellSSRs(ssrKeys, currencyCode, tabSessionId);
        } else {
          const ssrKeys = buildPayload();
          await removeSssrsFromBookingInState({ ssrKeys }, tabSessionId);
        }

        dispatch(
          setFlexiSmartButtonStatus({
            isAdded: nextState,
            price,
            direction,
            cardId: cardKey,
            cartType,
            ssrCode: SSR_CODES.FLEXI,
          }),
        );

        const booking = await getBookingInstate(tabSessionId);
        if (booking) {
          dispatch(setBookingDetails(booking));
        }

        return true;
      } catch (error) {
        console.error("FlexiSmart SSR error:", error);
        dispatch(
          setFlexiSmartButtonStatus({
            isAdded,
            price,
            direction,
            cardId: cardKey,
            cartType,
            ssrCode: SSR_CODES.FLEXI,
          }),
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [loading, flexiSsr, dispatch, direction, buildPayload, currencyCode, tabSessionId],
  );

  return {
    loading,
    flexiSsr,
    toggleFlexiSmart,
    ssrKeys: buildPayload(),
  };
}
